import { Box, Stack, Typography, Divider, Chip, Link as MuiLink, Tooltip } from '@mui/material';
import { getFormattedDateUTC } from 'utils/getFormattedDate';
import { useTheme, alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import {
  UserOutlined,
  IdcardOutlined,
  HomeOutlined,
  CalendarOutlined,
  ShopOutlined,
  FileTextOutlined,
  DollarOutlined,
  TagOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import VisibilityBadge from './UserViewBadge';
import { ApplicantData } from '../types';

interface Props {
  data: ApplicantData;
}

const Row = ({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string }) => {
  const theme = useTheme();
  if (!value) return null;
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minHeight: 26, py: 0.25 }}>
      <Box sx={{
        color: theme.palette.info.main,
        fontSize: 13,
        width: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Typography sx={{
        color: alpha(theme.palette.text.primary, 0.7),
        minWidth: 88,
        fontSize: '0.78rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.7,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: 1.4,
        flex: 1,
        minWidth: 0,
      }}>
        {value}
      </Typography>
    </Stack>
  );
};

const SectionHeader = ({ icon, label }: { icon: React.ReactNode; label: string }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={0.875} alignItems="center" sx={{ mb: 0.5 }}>
      <Box sx={{
        width: 18,
        height: 18,
        borderRadius: 0.75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(theme.palette.info.main, 0.18),
        color: theme.palette.info.main,
      }}>
        {icon}
      </Box>
      <Typography sx={{
        fontWeight: 800,
        fontSize: '0.78rem',
        letterSpacing: 1.1,
        textTransform: 'uppercase',
        color: alpha(theme.palette.text.primary, 0.88),
      }}>
        {label}
      </Typography>
    </Stack>
  );
};

const formatMoney = (amount: number | undefined): string | undefined => {
    if (amount === undefined || amount === null || Number.isNaN(amount)) return undefined;
    if (amount === 0) return undefined;
    const isWhole = Number.isInteger(amount);
    return `$${amount.toLocaleString(undefined, {
        minimumFractionDigits: isWhole ? 0 : 2,
        maximumFractionDigits: isWhole ? 0 : 2,
    })}`;
};

const formatDob = (dob: string | undefined): string | undefined => {
  if (!dob) return undefined;
  const iso = dob.includes('T') ? dob : `${dob}T00:00:00Z`;
  return getFormattedDateUTC(iso) || dob;
};

const ApplicantCard = ({ data }: Props) => {
  const theme = useTheme();
  const application = data?.application;
  const owner = data?.owner;

  if (!application || !owner) {
    return null;
  }

  const addressLine = owner.address
    ? [owner.address.line1, [owner.address.city, owner.address.state, owner.address.zip].filter(Boolean).join(', ')]
      .filter(Boolean)
      .join(' · ')
    : undefined;

  const statusColor =
    (application.status?.color as string | undefined) || theme.palette.info.main;

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        bgcolor: alpha(theme.palette.info.main, 0.05),
        border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
        borderLeft: `3px solid ${theme.palette.info.main}`,
        mb: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.75, pt: 1.25, pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.info.main, 0.18),
            }}
          >
            <FileTextOutlined style={{ fontSize: 14, color: theme.palette.info.main }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '0.82rem',
              letterSpacing: 1.1,
              textTransform: 'uppercase',
              color: alpha(theme.palette.text.primary, 0.9),
            }}
          >
            Application ·{' '}
            <Tooltip title="Open application in a new tab" arrow>
              <MuiLink
                component={RouterLink}
                to={`/applications/${application.id}`}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  color: theme.palette.info.main,
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {`#${application.number}`}
                <ExportOutlined style={{ fontSize: 10 }} />
              </MuiLink>
            </Tooltip>
          </Typography>
        </Stack>
        <VisibilityBadge mode="hidden_from_ai" />
      </Stack>

      <Divider sx={{ borderColor: alpha(theme.palette.info.main, 0.15) }} />

      <Box sx={{ px: 1.75, py: 1.25 }}>
        <Stack spacing={0.25} sx={{ mb: 1.25 }}>
          <Row
            icon={<ShopOutlined />}
            label="Merchant"
            value={(() => {
              const legal = application.merchantName;
              const dba = application.dbaName;
              if (!legal) return dba;
              if (dba && dba.trim().toLowerCase() !== legal.trim().toLowerCase()) {
                return `${legal} (dba ${dba})`;
              }
              return legal;
            })()}
          />
          <Row
            icon={<DollarOutlined />}
            label="Offer"
            value={(() => {
              const amt = application.fundingAmount;
              const rate = application.factorRate;
              const term = application.term;
              if (amt == null) return undefined;
              const fmtAmt = formatMoney(amt)!;
              if (rate && term) {
                const daily = (amt * rate) / term;
                return `${fmtAmt} × ${Number(rate).toFixed(3)} × ${term} d, ${formatMoney(daily)} daily pmt`;
              }
              if (term) return `${fmtAmt} · ${term} d`;
              return fmtAmt;
            })()}
          />
            {application.status?.description && (
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minHeight: 26, py: 0.25 }}>
                    <Box sx={{ width: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <TagOutlined style={{ fontSize: 13, color: theme.palette.info.main }} />
                    </Box>
                    <Typography sx={{
                        color: theme.palette.text.disabled,
                        minWidth: 88,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                    }}>
                        Status
                    </Typography>
                    <Chip
                        label={application.status.description}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            bgcolor: alpha(statusColor, 0.15),
                            color: statusColor,
                            '& .MuiChip-label': { px: 0.875 },
                        }}
                    />
                </Stack>
            )}
        </Stack>

        <Divider sx={{ borderColor: alpha(theme.palette.info.main, 0.12), mb: 1 }} />

        <SectionHeader icon={<IdcardOutlined style={{ fontSize: 11 }} />} label="Owner" />
        <Stack spacing={0.25}>
          <Row icon={<UserOutlined />} label="Name" value={owner.fullName} />
          <Row icon={<CalendarOutlined />} label="DOB" value={formatDob(owner.dob)} />
          <Row
            icon={<IdcardOutlined />}
            label="SSN"
            value={owner.ssnLast4 ? `••• •• ${owner.ssnLast4}` : undefined}
          />
          <Row icon={<HomeOutlined />} label="Address" value={addressLine} />
        </Stack>
      </Box>
    </Box>
  );
};

export default ApplicantCard;
